import { type RefObject, useEffect, useMemo, useState } from 'react';
import type { Breakpoint } from 'antd';

export interface ContainerWidthType {
  width: number;
  currentBreakpoint: Breakpoint;
  breakpointsGreater: Record<Breakpoint, boolean>;
  breakpointsLess: Record<Breakpoint, boolean>;
}

const breakpoints: Record<Breakpoint, number> = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

type CalculationType = 'greater' | 'less' | 'equal';

const isBreakpointWidth = (
  width: number,
  breakpointKey: Breakpoint,
  type: CalculationType
) =>
  type === 'greater'
    ? breakpoints[breakpointKey] >= width
    : type === 'less'
      ? breakpoints[breakpointKey] <= width
      : breakpoints[breakpointKey] === width;

const isBreakpointsEqualToWidth = (
  width: number,
  type: CalculationType
): Record<Breakpoint, boolean> => {
  return Object.keys(breakpoints).reduce(
    (acc, currentBreakpoint) => {
      const breakpointKey = currentBreakpoint as Breakpoint;
      acc[breakpointKey] = isBreakpointWidth(width, breakpointKey, type);
      return acc;
    },
    {
      xs: false,
      sm: false,
      md: false,
      lg: false,
      xl: false,
      xxl: false,
    } as Record<Breakpoint, boolean>
  );
};

const getBreakpoint = (width: number): Breakpoint => {
  const currentBreakpoints = isBreakpointsEqualToWidth(width, 'greater');
  const matchedBreakpoint =
    (Object.entries(currentBreakpoints).find((value) => value[1])?.[0] as
      | Breakpoint
      | undefined) ?? 'xs';
  return matchedBreakpoint;
};

export const useContainerWidth = (ref: RefObject<HTMLElement>) => {
  const [width, setWidth] = useState(0);

  const currentBreakpoint = useMemo(() => getBreakpoint(width), [width]);
  const breakpointsGreater = useMemo(
    () => isBreakpointsEqualToWidth(width, 'greater'),
    [width]
  );
  const breakpointsLess = useMemo(
    () => isBreakpointsEqualToWidth(width, 'less'),
    [width]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateWidth = () => {
      setWidth(el.getBoundingClientRect().width);
    };

    updateWidth(); // Set initial width

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return { width, currentBreakpoint, breakpointsGreater, breakpointsLess };
};
